import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, LoadingScreen, EmptyState } from "@/components/common";

const CATEGORIES = ["Theft", "Unauthorized Entry", "Fire", "Accident", "Fight", "Property Damage", "Missing Person", "Security Breach", "Other"];

export default function Incidents() {
  const [rows, setRows] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ contract_id: "", category: "", description: "", location: "", incident_datetime: "", guard_name: "", supervisor: "" });

  const load = () => api.get("/incidents/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/contracts/mine").then((r) => setContracts(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.category || !f.description || !f.location) return toast.error("Fill category, description and location");
    try { await api.post("/incidents", f); toast.success("Incident reported. An incident report has been generated."); setOpen(false); setF({ contract_id: "", category: "", description: "", location: "", incident_datetime: "", guard_name: "", supervisor: "" }); load(); }
    catch (e) { toast.error("Failed to report"); }
  };

  return (
    <DashboardShell menu={customerMenu} title="Incident Management">
      <PageHeader title="Incident Reports" subtitle="Report and track security incidents."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-600" data-testid="report-incident-btn"><Plus size={16} className="mr-1" /> Report Incident</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Report an Incident</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Category *</Label>
                  <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                    <SelectTrigger data-testid="inc-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Location *</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="inc-location" /></div>
                  <div><Label className="text-xs">Date & Time</Label><Input type="datetime-local" value={f.incident_datetime} onChange={(e) => setF({ ...f, incident_datetime: e.target.value })} data-testid="inc-datetime" /></div>
                  <div><Label className="text-xs">Guard Name</Label><Input value={f.guard_name} onChange={(e) => setF({ ...f, guard_name: e.target.value })} data-testid="inc-guard" /></div>
                  <div><Label className="text-xs">Supervisor</Label><Input value={f.supervisor} onChange={(e) => setF({ ...f, supervisor: e.target.value })} data-testid="inc-supervisor" /></div>
                </div>
                <div><Label className="text-xs">Description *</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="inc-description" /></div>
              </div>
              <DialogFooter><Button className="bg-brand hover:bg-brand-600" onClick={submit} data-testid="submit-incident-btn">Generate Report</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="No incidents reported" hint="Report any security incident to generate a formal report and notify your agency." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((i) => (
            <div key={i.id} className="bg-white border border-navy-200 rounded-xl p-5" data-testid={`incident-${i.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><AlertTriangle size={18} /></span><div><h3 className="font-heading font-semibold text-navy-900">{i.category}</h3><p className="text-xs text-navy-500">{i.location}</p></div></div>
                <StatusPill status={i.status} />
              </div>
              <p className="text-sm text-navy-600 mt-3">{i.description}</p>
              <div className="mt-3 text-xs text-navy-400">{i.incident_datetime || i.created_at?.slice(0, 10)} {i.guard_name && `· Guard: ${i.guard_name}`}</div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
